/**
 * The conversation with a supplier about one order, and the box to answer in.
 *
 * Opening it marks the supplier's messages as read — they see "read by
 * Zaprinta" on their side — so it is only loaded when someone actually opens
 * the popup, never prefetched with the order list.
 *
 * Shown two ways: as a popup from the portal accounts screen and from a
 * notification, and inline in an order's "supplier order" tab.
 */
define('portalConversationView',[
  'module',
  'marionette',
  'underscore',
  'jquery',
  'app',
  'dateUtils',
  'dialogView',
  'template!portalConversationTpl',
  'entities/suppliers/portal_orders'
], function (
  Module,
  Marionette,
  _,
  $,
  App,
  DateConverter,
  DialogView,
  conversationTpl
) {
  'use strict';

  /** Matches the server's limit, so the box refuses what the server would. */
  var MAX_LENGTH = 4000;

  Module.exports = DialogView.extend({
    template: conversationTpl,
    className: 'portal-conversation-popup',

    ui: {
      thread: '.js-thread',
      draft: '.js-draft',
      send: '.js-send',
      error: '.js-error'
    },

    events: {
      'click .js-cancel-popup': 'hide',
      'click @ui.send': 'onSend',
      'input @ui.draft': 'onDraft',
      'keydown @ui.draft': 'onKeydown'
    },

    initialize: function (options) {
      this.order = options.order || {};
      // Inline: shown inside the order itself, where the order needs no
      // introduction and there is nothing to close.
      this.inline = options.inline === true;
      this.messages = [];
    },

    serializeData: function () {
      return {
        inline: this.inline,
        purchaseOrderId: this.order.purchaseOrderId,
        productCode: this.order.productCode,
        productName: this.order.productName,
        supplierCode: this.order.supplierCode,
        maxLength: MAX_LENGTH
      };
    },

    onShow: function () {
      this.ui.send.prop('disabled', true);
      this.load();
    },

    load: function () {
      $.when(App.request('portal-orders:messages', this.order.secId))
        .done(_.bind(function (messages) {
          if (this.isDestroyed) {
            return;
          }
          this.messages = messages || [];
          this.renderThread();
        }, this))
        .fail(_.bind(function () {
          if (this.isDestroyed) {
            return;
          }
          this.ui.error.text(_.i18n('portalMessages.loadFailed', null,
            'Could not read this conversation. Close it and try again.'));
        }, this));
    },

    /**
     * Built node by node with text(), never from a template string: the body is
     * whatever a supplier typed, and it is rendered inside the dashboard.
     *
     * Only the thread is redrawn, not the view — a full render would throw away
     * a half-written reply.
     */
    renderThread: function () {
      var $thread = this.ui.thread.empty();

      if (this.messages.length === 0) {
        $thread.append($('<p class="conversation__empty"></p>').text(
          _.i18n('portalMessages.empty', null,
            'Nothing has been said about this order yet. A message sent here reaches the supplier in their portal.')));
        return;
      }

      _.each(this.messages, function (message) {
        var $message = $('<div class="conversation__message"></div>')
          .toggleClass('is-ours', !message.fromSupplier);

        var author = message.authorName ||
          (message.fromSupplier ? _.i18n('portalMessages.supplier', null, 'Supplier') : 'Zaprinta');

        $('<div class="conversation__meta"></div>')
          .append($('<span class="conversation__author"></span>').text(author))
          .append($('<span class="conversation__when"></span>').text(
            message.createdAt ? DateConverter.toMDYHHMMFormat(message.createdAt) : ''))
          .appendTo($message);

        $('<p class="conversation__body"></p>').text(message.body || '').appendTo($message);

        // Only on ours: whether the supplier has seen the answer is the thing
        // support cannot know any other way.
        if (!message.fromSupplier) {
          $('<span class="conversation__status"></span>').text(message.readAt ?
            _.i18n('portalMessages.readBySupplier', null, 'Read by the supplier') :
            _.i18n('portalMessages.notReadYet', null, 'Not read yet')).appendTo($message);
        }

        $thread.append($message);
      });

      // The newest is the one that matters, and it is at the bottom.
      $thread.scrollTop($thread.prop('scrollHeight'));
    },

    onDraft: function () {
      this.ui.send.prop('disabled', this.sending === true || $.trim(this.ui.draft.val()) === '');
    },

    /** Ctrl/Cmd+Enter sends; plain Enter stays a new line. */
    onKeydown: function (event) {
      if (event.keyCode === 13 && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.onSend();
      }
    },

    onSend: function () {
      var body = $.trim(this.ui.draft.val());
      if (!body || this.sending) {
        return;
      }

      this.sending = true;
      this.ui.send.prop('disabled', true);
      this.ui.error.text('');

      $.when(App.request('portal-orders:reply', this.order.secId, body))
        .done(_.bind(function (message) {
          if (this.isDestroyed) {
            return;
          }
          this.messages.push(message);
          this.renderThread();
          // Cleared only once it is safely sent.
          this.ui.draft.val('');
          this.replied = true;
        }, this))
        .fail(_.bind(function () {
          if (this.isDestroyed) {
            return;
          }
          this.ui.error.text(_.i18n('portalMessages.sendFailed', null,
            'The reply was not sent. Your text is still here — try again.'));
        }, this))
        .always(_.bind(function () {
          this.sending = false;
          if (!this.isDestroyed) {
            this.onDraft();
          }
        }, this));
    },

    /**
     * The mixin's hide() runs twice — once when called, once again from the
     * modal's own hidden event — so the screen behind is told once only.
     */
    onHide: function () {
      if (this.closedTold) {
        return;
      }
      this.closedTold = true;
      this.trigger('closed');
    }
  });
});

